import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router, ActivatedRoute } from '@angular/router';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { DataExchangeRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-dto';
import { DataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';

@Component({
  selector: 'app-data-exchange-requests',
  templateUrl: './data-exchange-requests.component.html',
  styleUrls: ['./data-exchange-requests.component.scss'],
})
export class DataExchangeRequestsComponent implements OnInit, OnDestroy {

  readonly displayedColumns: string[] = ['expand', 'id', 'name', 'counterpart', 'subRequestCount', 'status', 'actions'];

  readonly systemColumns    = ['id', 'brandName', 'productName', 'articleNumber', 'subRequestStatus', 'showSubRequestComments', 'approveSubRequest', 'navigateToSubRequest'];
  readonly unitColumns      = ['id', 'packagingTypeName', 'brandName', 'productName', 'articleNumber', 'subRequestStatus', 'showSubRequestComments', 'approveSubRequest', 'navigateToSubRequest'];
  readonly componentColumns = ['id', 'packagingComponentCategoryName', 'articleName', 'articleNumber', 'subRequestStatus', 'showSubRequestComments', 'approveSubRequest', 'navigateToSubRequest'];
  readonly materialColumns  = ['id', 'articleName', 'articleNumber', 'manufacturerName', 'subRequestStatus', 'showSubRequestComments', 'approveSubRequest', 'navigateToSubRequest'];

  readonly systemColumnsReceived    = ['id', 'brandName', 'productName', 'articleNumber', 'subRequestStatus', 'showSubRequestComments', 'navigateToSubRequest'];
  readonly unitColumnsReceived      = ['id', 'packagingTypeName', 'brandName', 'productName', 'articleNumber', 'subRequestStatus', 'showSubRequestComments', 'navigateToSubRequest'];
  readonly componentColumnsReceived = ['id', 'packagingComponentCategoryName', 'articleName', 'articleNumber', 'subRequestStatus', 'showSubRequestComments', 'navigateToSubRequest'];
  readonly materialColumnsReceived  = ['id', 'articleName', 'articleNumber', 'manufacturerName', 'subRequestStatus', 'showSubRequestComments', 'navigateToSubRequest'];

  selectedTabIndex = 0;
  showClosedAndDeleted = false;

  sentDataSource = new MatTableDataSource<DataExchangeRequestDto>();
  receivedDataSource = new MatTableDataSource<DataExchangeRequestDto>();

  private allSentRequests: DataExchangeRequestDto[] = [];
  private allReceivedRequests: DataExchangeRequestDto[] = [];
  private sentSubscription?: Subscription;
  private receivedSubscription?: Subscription;
  private queryParamsSubscription?: Subscription;

  constructor(
    private dataExchangeRequestApiService: DataExchangeRequestApiService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      this.selectedTabIndex = +(params['tab'] ?? 0);
    });
    this.loadData();
  }

  loadData(): void {
    this.sentSubscription?.unsubscribe();
    this.receivedSubscription?.unsubscribe();
    this.spinner.show();
    this.sentSubscription = this.dataExchangeRequestApiService.getSentRequests(true).subscribe({
      next: requests => {
        this.allSentRequests = requests;
        this.applyDataFilter();
        this.spinner.hide();
      },
      error: () => this.spinner.hide()
    });
    this.receivedSubscription = this.dataExchangeRequestApiService.getReceivedRequests(true).subscribe({
      next: requests => {
        this.allReceivedRequests = requests;
        this.applyDataFilter();
      },
      error: () => this.spinner.hide()
    });
  }

  toggleShowClosedAndDeleted(): void {
    this.showClosedAndDeleted = !this.showClosedAndDeleted;
    this.applyDataFilter();
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.router.navigate([], { queryParams: { tab: index }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  private applyDataFilter(): void {
    const hidden = [DataExchangeRequestStatusDto.Closed, DataExchangeRequestStatusDto.Deleted];
    const process = (requests: DataExchangeRequestDto[]) => {
      const filtered = this.showClosedAndDeleted ? requests : requests.filter(r => !hidden.includes(r.status));
      return filtered.sort((a, b) => a.status - b.status || a.id - b.id);
    };
    this.sentDataSource = new MatTableDataSource(process(this.allSentRequests));
    this.receivedDataSource = new MatTableDataSource(process(this.allReceivedRequests));
  }

  ngOnDestroy(): void {
    this.queryParamsSubscription?.unsubscribe();
    this.sentSubscription?.unsubscribe();
    this.receivedSubscription?.unsubscribe();
  }
}

